/**
 * `hermes.chat` — design-intelligence §2.3, on the wire.
 *
 * ## The one thing this surface must never let a client do
 *
 * **Execute a write the user did not confirm.** §2.3 permits exactly two
 * mutations inside a turn (`create_task`, `snooze_contact`, both of which only
 * touch the user's own queue and both reversible in a tap); everything that
 * could reach another human comes back as a `proposedAction` and needs a SECOND
 * call.
 *
 * So `chat.confirm` takes an **actionId and nothing else**. There is no payload
 * parameter and there is deliberately no way to add one: the thing that executes
 * is the row the server wrote when the model proposed it. A contract that
 * accepted the payload back from the client would turn "the user confirmed what
 * Hermes suggested" into "the client may execute anything it can describe",
 * which is the whole gate.
 *
 * ## What is deliberately NOT here
 *
 *  - **No streaming.** A turn is bounded at eight tool calls and answers in one
 *    response; SSE would add a second transport and a resumption story for a
 *    latency budget the bound already keeps small.
 *  - **No `chat.delete`.** A transcript is the audit trail behind actions the
 *    user took. `archived` hides a session; nothing erases one.
 *  - **No tool-invocation procedure.** Tools are the model's, not the client's.
 *    A client that could call `create_task` through this surface would be using
 *    a chat endpoint to bypass `followUps`.
 */

import { z } from 'zod';
import { isoDateTimeSchema, jsonObjectSchema, nonBlankString, uuidSchema } from './common.ts';

export const CHAT_MESSAGE_ROLES = ['user', 'assistant', 'tool'] as const;
export const chatMessageRoleSchema = z.enum(CHAT_MESSAGE_ROLES);

/** Mirrors `TOOL_EFFECTS` in `@crm/intelligence`. The UI badges on it. */
export const CHAT_ACTION_KINDS = ['schedule_follow_up', 'draft_message'] as const;
export const chatActionKindSchema = z.enum(CHAT_ACTION_KINDS);

export const CHAT_ACTION_STATUSES = ['proposed', 'confirmed', 'cancelled', 'expired'] as const;
export const chatActionStatusSchema = z.enum(CHAT_ACTION_STATUSES);

/**
 * Why a turn produced no reply. Every one of these is shown to the user rather
 * than swallowed — a chat that silently skips a turn is indistinguishable from
 * one that lost it.
 */
export const CHAT_DROP_REASONS = [
  'guard_failed',
  'empty_reply',
  'model_error',
  'not_configured',
  'budget_exhausted',
  'input_too_large',
  'closed_weight_blocked',
  'queued',
] as const;
export const chatDropReasonSchema = z.enum(CHAT_DROP_REASONS);

export const chatSessionSchema = z.object({
  id: uuidSchema,
  /** Derived from the first user message. Null until there is one. */
  title: z.string().nullable(),
  messageCount: z.int().min(0),
  /** Cumulative across the session; the eight-call bound is per TURN. */
  toolCallsUsed: z.int().min(0),
  personaVersion: z.int().min(0).nullable(),
  lastMessageAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
});

/**
 * One turn, INCLUDING the tool traffic.
 *
 * The tool calls are on the wire because §2.4's evidence spine applies here too:
 * a client renders "Hermes looked at Priya's dossier" under the answer, and a
 * user who cannot see what was consulted cannot judge the answer. A transcript
 * that carried only prose would make every reply unauditable.
 */
export const chatMessageSchema = z.object({
  id: uuidSchema,
  seq: z.int().min(0),
  role: chatMessageRoleSchema,
  content: z.string(),
  /** Tool turns. */
  toolName: z.string().nullable(),
  toolOk: z.boolean().nullable(),
  /** Assistant turns: what the model asked for, verbatim. */
  toolCalls: z.array(z.object({ name: z.string(), arguments: z.string() })).nullable(),
  modelId: z.string().nullable(),
  toolCallsUsed: z.int().min(0).nullable(),
  toolLimitReached: z.boolean(),
  droppedReason: chatDropReasonSchema.nullable(),
  createdAt: isoDateTimeSchema,
});

/**
 * A write the model wants to make and a human has to approve.
 *
 * `payload` is opaque `jsonObject` on purpose: the client RENDERS it (a due
 * date, a channel, a body) but never sends it back. The server executes its own
 * stored copy.
 */
export const chatProposedActionSchema = z.object({
  id: uuidSchema,
  kind: chatActionKindSchema,
  status: chatActionStatusSchema,
  contactId: uuidSchema.nullable(),
  contactName: z.string().nullable(),
  payload: jsonObjectSchema,
  /** What executing it produced. Null until confirmed. */
  resultRef: jsonObjectSchema.nullable(),
  /** A proposal nobody looked at must not execute a month later. */
  expiresAt: isoDateTimeSchema,
  createdAt: isoDateTimeSchema,
});

export const chatSendInputSchema = z.object({
  /** Absent starts a new conversation and the response names the new id. */
  sessionId: uuidSchema.optional(),
  message: nonBlankString(4_000, 'message'),
});

export const chatSendOutputSchema = z.object({
  sessionId: uuidSchema,
  /** Null when the turn was dropped: there is no assistant message to show. */
  reply: z.string().nullable(),
  /** Present when `reply` is null, and always accompanied by `message`. */
  droppedReason: chatDropReasonSchema.nullable(),
  /** Human-readable, safe to render. Never a stack trace. */
  message: z.string().nullable(),
  /** The turns this call produced, in order, tool traffic included. */
  messages: z.array(chatMessageSchema),
  /** Awaiting confirmation. Rendered as buttons; executed by `chat.confirm`. */
  proposedActions: z.array(chatProposedActionSchema),
  toolCallsUsed: z.int().min(0),
  /** True when the model wanted more tools than §2.3's bound allows. */
  toolLimitReached: z.boolean(),
  modelCalls: z.int().min(0),
});

export const chatSessionsInputSchema = z.object({
  limit: z.int().min(1).max(100).default(30),
  includeArchived: z.boolean().default(false),
});

export const chatSessionsOutputSchema = z.object({
  items: z.array(chatSessionSchema),
});

export const chatHistoryInputSchema = z.object({
  sessionId: uuidSchema,
  /** The newest N turns. A long session is windowed, not truncated at the front. */
  limit: z.int().min(1).max(200).default(60),
});

export const chatHistoryOutputSchema = z.object({
  session: chatSessionSchema,
  messages: z.array(chatMessageSchema),
  actions: z.array(chatProposedActionSchema),
});

/** The gate: an id, and nothing a client could substitute a payload into. */
export const chatActionInputSchema = z.object({ actionId: uuidSchema });

export const chatActionOutputSchema = z.object({
  action: chatProposedActionSchema,
  /** The follow-up a confirmed `schedule_follow_up` created. */
  followUpId: uuidSchema.nullable(),
  /** The draft a confirmed `draft_message` created, ready for the send gate. */
  draftId: uuidSchema.nullable(),
});

export type ChatSessionDto = z.output<typeof chatSessionSchema>;
export type ChatMessageDto = z.output<typeof chatMessageSchema>;
export type ChatProposedActionDto = z.output<typeof chatProposedActionSchema>;
